import {
  Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DDIPanel, type SafetySnapshot } from '@/components/clinical/DDIPanel';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { clinician } from '@/api/endpoints';
import { formatDate, formatDateTime } from '@/utils/format';
import type { Prescription, PrescriptionItem, PrescriptionStatus } from '@/types/medical';

export function ClinicianPrescriptionDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();

  const [revokeOpen, setRevokeOpen] = useState(false);

  const q = useApiQuery({
    queryKey: ['clinician', 'prescriptions', id],
    queryFn:  () => api_get_rx(id),
    enabled:  Boolean(id),
  });

  const revokeMut = useApiMutation({
    mutationFn: (reason: string) => clinician.prescriptions.revoke(id, reason),
    onSuccess: () => {
      toast.success('Prescription revoked.');
      qc.invalidateQueries({ queryKey: ['clinician', 'prescriptions'] });
      setRevokeOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Prescription"
        breadcrumbs={[
          { label: 'Prescriptions', to: '/clinician/prescriptions' },
          { label: 'Detail' },
        ]}
      />

      <DataState query={q}>
        {(rx) => (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <HeaderCard rx={rx} />
              <ItemsCard items={rx.items ?? []} />
              {rx.safety_check_snapshot &&
               'overall_severity' in (rx.safety_check_snapshot as object) && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary"
                                sx={{ display: 'block', mb: 1.5 }}>
                      Safety check at signing
                    </Typography>
                    <DDIPanel snapshot={rx.safety_check_snapshot as SafetySnapshot} />
                  </CardContent>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <ActionsCard
                rx={rx}
                onRevoke={() => setRevokeOpen(true)}
                onAmend={() => {/* Tour 3+ — wire amend route */}}
              />
              <SigningCard rx={rx} />
            </Grid>
          </Grid>
        )}
      </DataState>

      <ConfirmDialog
        open={revokeOpen}
        title="Revoke prescription?"
        description="Revocation is permanent and notifies the patient and (if applicable) the e-prescription registry. Provide a clinical reason."
        destructive
        reasonRequired
        confirmLabel="Revoke"
        onCancel={() => setRevokeOpen(false)}
        onConfirm={(reason) => revokeMut.mutateAsync(reason ?? '')}
      />
    </>
  );
}

// We don't have a clinician.prescriptions.get yet, so define inline.
import { api } from '@/api/client';
const api_get_rx = (id: string) => api.get<Prescription>(`/api/medical/clinician/prescriptions/${id}/`);

function HeaderCard({ rx }: { rx: Prescription }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Prescription
            </Typography>
            <Typography variant="h5" component="h2" sx={{ mb: 0.5 }} className="tabular">
              {rx.registry_external_id || rx.id.slice(0, 8)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Patient #{rx.patient} · {rx.jurisdiction_code}
              {rx.issued_at && ` · issued ${formatDateTime(rx.issued_at)}`}
              {rx.valid_until && ` · valid until ${formatDate(rx.valid_until)}`}
            </Typography>
          </Box>
          <StatusChip status={rx.status} />
        </Stack>
        {rx.revoked_at && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="error">
              Revoked {formatDateTime(rx.revoked_at)}
              {rx.revocation_reason && ` — ${rx.revocation_reason}`}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function ItemsCard({ items }: { items: PrescriptionItem[] }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}>
          Items
        </Typography>
        <Stack divider={<Divider />} spacing={1.5}>
          {items.map((it) => (
            <Box key={it.id}>
              <Typography variant="subtitle1" fontWeight={600}>
                {it.drug_name_snapshot}
                {it.drug_strength_snapshot && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}· {it.drug_strength_snapshot}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary"
                          className="tabular">
                ATC {it.atc_snapshot} · {it.drug_form_snapshot}
              </Typography>
              <Stack direction="row" spacing={3} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Field label="Dose" value={it.dose_text || '—'} />
                <Field label="Qty" value={it.quantity_text || '—'} />
                <Field label="Refills" value={String(it.refills)} />
                {it.indication_icd_code && (
                  <Field label="ICD" value={it.indication_icd_code} />
                )}
              </Stack>
              {it.notes_to_pharmacist && (
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}>
                  Pharmacist note: {it.notes_to_pharmacist}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" className="tabular" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );
}

function ActionsCard({
  rx, onRevoke, onAmend,
}: {
  rx: Prescription;
  onRevoke: () => void;
  onAmend: () => void;
}) {
  const live = ['signed', 'registered', 'partly_dispensed'].includes(rx.status);
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}>
          Actions
        </Typography>
        <Stack spacing={1}>
          <Button
            variant="outlined" fullWidth disabled={!live}
            startIcon={<EditIcon />} onClick={onAmend}
          >
            Amend
          </Button>
          <Button
            variant="outlined" color="error" fullWidth disabled={!live}
            startIcon={<CancelIcon />} onClick={onRevoke}
          >
            Revoke
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SigningCard({ rx }: { rx: Prescription }) {
  if (!rx.signing_key_fingerprint_at_issue) return null;
  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <VerifiedIcon color="success" fontSize="small" />
          <Typography variant="overline" color="text.secondary">
            Signature
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block' }} className="tabular">
          algorithm: {rx.signature_algorithm}
        </Typography>
        <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', wordBreak: 'break-all' }}
                    className="tabular">
          fingerprint: {rx.signing_key_fingerprint_at_issue}
        </Typography>
        {rx.registry_submitted_at && (
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}>
            Registered with {rx.registry_code} at {formatDateTime(rx.registry_submitted_at)}.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function StatusChip({ status }: { status: PrescriptionStatus }) {
  const m: Record<PrescriptionStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
    draft:           { label: 'draft', color: 'warning' },
    signed:          { label: 'signed', color: 'primary' },
    registered:      { label: 'registered', color: 'primary' },
    partly_dispensed:{ label: 'partly filled', color: 'primary' },
    dispensed:       { label: 'dispensed', color: 'success' },
    cancelled:       { label: 'cancelled', color: 'default' },
    expired:         { label: 'expired', color: 'default' },
    failed:          { label: 'failed', color: 'error' },
  };
  const c = m[status];
  return <Chip label={c.label} color={c.color} variant="outlined" />;
}
